import {
  Image,
  FileText,
  Users,
  Star,
  Calendar,
  BookOpen,
  ImageIcon,
  UserCog,
  Menu,
  FilePlus,
  CreditCard,
  Building,
  Library,
  Badge,
  LayoutGrid,
  ListChecks,
  Upload,
  Award,
  Languages,
  FileQuestion,
  Medal,
  ClipboardCheck,
  GitBranch,
  Key,
  Mail,
  FolderOpen,
  QrCode,
  CalendarDays,
  MessageSquare,
  Globe,
  Bell,
  Megaphone,
  GraduationCap,
  Settings,
  Database,
  Rocket,
  Share2,
  UserPlus,
  Inbox,
  Link2,
  History,
  Wallet,
} from 'lucide-react';

// Route definitions organized into cockpit dashboards.
// Each cockpit groups related sections; every section maps to a URL path and
// editor component. Section ids and paths are STABLE — they are preserved across
// cockpit reorganizations so existing deep links keep working even when a section
// is regrouped under a different cockpit (see the admin-cockpit-refactor IA).
export const adminRoutes = {
  students: {
    label: 'Students & Membership',
    shortLabel: 'Students',
    icon: Users,
    path: '/admin/students',
    sections: [
      { id: 'students', label: 'Student List', icon: Users, path: '/admin/students/list', description: 'Belt progression' },
      {
        id: 'customers',
        label: 'Customers',
        icon: CreditCard,
        path: '/admin/students/customers',
        description: 'Subscriptions',
        subSections: [
          { id: 'customers', label: 'Directory', icon: Users, path: '/admin/students/customers?view=customers' },
          { id: 'settings', label: 'Billing settings', icon: Settings, path: '/admin/students/customers?view=settings' },
        ],
      },
      { id: 'registrations', label: 'Registrations', icon: UserPlus, path: '/admin/students/registrations', description: 'Approve new members' },
      { id: 'addstudentreviews', label: 'Add Student Reviews', icon: UserPlus, path: '/admin/students/add-student-reviews', description: 'Approve parent-submitted students' },
      { id: 'historyreviews', label: 'History Reviews', icon: History, path: '/admin/students/history-reviews', description: 'Review submitted history', requiresRole: ['instructor', 'owner'] },
      { id: 'belts', label: 'Belt Ranks', icon: Award, path: '/admin/students/belts', description: 'Configure belts' },
      { id: 'resourcelinks', label: 'Resource Links', icon: Link2, path: '/admin/students/resource-links', description: 'Shareable deep links' },
    ],
  },
  learning: {
    label: 'Learning & Curriculum',
    shortLabel: 'Learning',
    icon: GraduationCap,
    path: '/admin/learning',
    sections: [
      { id: 'vocabulary', label: 'Vocabulary', icon: Languages, path: '/admin/learning/vocabulary', description: 'Japanese terms' },
      { id: 'quizzes', label: 'Quizzes', icon: FileQuestion, path: '/admin/learning/quizzes', description: 'Knowledge tests' },
      { id: 'scenarios', label: 'Scenarios', icon: GitBranch, path: '/admin/learning/scenarios', description: 'Branching stories' },
      { id: 'badges', label: 'Badges', icon: Medal, path: '/admin/learning/badges', description: 'Achievements' },
      { id: 'reviews', label: 'Answer Reviews', icon: ClipboardCheck, path: '/admin/learning/reviews', description: 'Grade responses' },
      // Moved from the Students route; path preserved for existing deep links.
      { id: 'resources', label: 'Resources', icon: Library, path: '/admin/students/resources', description: 'Training materials' },
    ],
  },
  website: {
    label: 'Website & Content',
    shortLabel: 'Website',
    icon: Globe,
    path: '/admin/website',
    sections: [
      { id: 'hero', label: 'Hero Section', icon: Image, path: '/admin/website/hero', description: 'Main homepage banner' },
      { id: 'trustbadges', label: 'Trust Badges', icon: Badge, path: '/admin/website/trust-badges', description: 'Hero section badges' },
      { id: 'whyowaza', label: 'Why Owaza', icon: LayoutGrid, path: '/admin/website/why-owaza', description: 'Feature cards' },
      { id: 'expectations', label: 'What to Expect', icon: ListChecks, path: '/admin/website/expectations', description: 'Steps & CTA' },
      { id: 'programs', label: 'Programs', icon: FileText, path: '/admin/website/programs', description: 'Martial arts programs' },
      { id: 'lineage', label: 'Lineage', icon: BookOpen, path: '/admin/website/lineage', description: 'History & instructors' },
      { id: 'gallery', label: 'Gallery', icon: ImageIcon, path: '/admin/website/gallery', description: 'Photos & videos' },
      { id: 'pages', label: 'Custom Pages', icon: FilePlus, path: '/admin/website/pages', description: 'Create new pages' },
      { id: 'navigation', label: 'Navigation', icon: Menu, path: '/admin/website/navigation', description: 'Menu links' },
      { id: 'announcements', label: 'Announcements', icon: Bell, path: '/admin/website/announcements', description: 'Homepage ribbon banners' },
      { id: 'medialibrary', label: 'Media Library', icon: FolderOpen, path: '/admin/website/media', description: 'Manage images' },
      // Moved from the Business cockpit; path preserved for existing deep links.
      { id: 'testimonials', label: 'Reviews', icon: Star, path: '/admin/business/reviews', description: 'Customer reviews' },
    ],
  },
  business: {
    label: 'Business & Operations',
    shortLabel: 'Business',
    icon: Building,
    path: '/admin/business',
    sections: [
      { id: 'business', label: 'Business Info', icon: Building, path: '/admin/business/info', description: 'Contact & settings' },
      { id: 'schedule', label: 'Schedule', icon: Calendar, path: '/admin/business/schedule', description: 'Weekly classes' },
      { id: 'qrcodes', label: 'QR Codes', icon: QrCode, path: '/admin/business/qr-codes', description: 'Print QR codes' },
    ],
  },
  outreach: {
    label: 'Outreach & Comms',
    shortLabel: 'Outreach',
    icon: Megaphone,
    path: '/admin/outreach',
    sections: [
      { id: 'inbox', label: 'Inbox', icon: Inbox, path: '/admin/outreach/inbox', description: 'View incoming emails' },
      { id: 'newsletter', label: 'Newsletter', icon: Mail, path: '/admin/outreach/newsletter', description: 'Email campaigns' },
      { id: 'socialmedia', label: 'Social Media', icon: Share2, path: '/admin/outreach/social', description: 'Create and schedule posts' },
      { id: 'events', label: 'Events', icon: CalendarDays, path: '/admin/outreach/events', description: 'Public events' },
      { id: 'feedback', label: 'Feedback', icon: MessageSquare, path: '/admin/outreach/feedback', description: 'Survey responses' },
    ],
  },
  finance: {
    label: 'Finance & Billing',
    shortLabel: 'Finance',
    icon: Wallet,
    path: '/admin/finance',
    sections: [
      // Moved from the Settings cockpit; path preserved for existing deep links.
      { id: 'stripe', label: 'Stripe', icon: CreditCard, path: '/admin/settings/stripe', description: 'Payment settings' },
    ],
  },
  settings: {
    label: 'Settings & Platform',
    shortLabel: 'Settings',
    icon: Settings,
    path: '/admin/settings',
    sections: [
      { id: 'adminusers', label: 'Admin Users', icon: UserCog, path: '/admin/settings/users', description: 'Admin access' },
      { id: 'apikeys', label: 'API Keys', icon: Key, path: '/admin/settings/api-keys', description: 'External services' },
      { id: 'publish', label: 'Publish', icon: Upload, path: '/admin/settings/publish', description: 'Deploy changes' },
      { id: 'backups', label: 'Database Backups', icon: Database, path: '/admin/settings/backups', description: 'MongoDB backups' },
      { id: 'deployments', label: 'Deployments', icon: Rocket, path: '/admin/settings/deployments', description: 'Deploy & monitor' },
      { id: 'email', label: 'Email', icon: Mail, path: '/admin/settings/email', description: 'Blocking & forwarding' },
    ],
  },
};

// Flat list of all sections for easy lookup
export const allSections = Object.values(adminRoutes).flatMap(
  (category) => category.sections
);

// Get section by ID
export const getSectionById = (sectionId) => {
  return allSections.find((section) => section.id === sectionId);
};

// Get section by path
export const getSectionByPath = (path) => {
  const normalizedPath = path.replace(/\/+$/, '') || '/';

  return allSections.find(
    (section) =>
      section.path === normalizedPath || normalizedPath.startsWith(`${section.path}/`)
  );
};

// Get category by section ID
export const getCategoryBySectionId = (sectionId) => {
  for (const [categoryId, category] of Object.entries(adminRoutes)) {
    if (category.sections.some((s) => s.id === sectionId)) {
      return categoryId;
    }
  }
  return null;
};

// Get category by path
export const getCategoryByPath = (path) => {
  const normalizedPath = path.replace(/\/+$/, '') || '/';

  for (const [categoryId, category] of Object.entries(adminRoutes)) {
    if (
      category.path === normalizedPath ||
      category.sections.some(
        (section) =>
          section.path === normalizedPath || normalizedPath.startsWith(`${section.path}/`)
      )
    ) {
      return categoryId;
    }
  }
  return null;
};

// Section metadata for headers/descriptions
export const sectionMeta = {
  hero: { title: 'Hero Section', description: 'Update the main hero section content and image' },
  trustbadges: { title: 'Trust Badges', description: 'Manage the trust indicator badges above the hero title' },
  whyowaza: { title: 'Why Choose Owaza', description: 'Manage the feature cards in the "Why Choose Owaza" section' },
  expectations: { title: 'What to Expect', description: 'Manage the expectation steps and CTA button' },
  programs: { title: 'Programs', description: 'Manage martial arts programs and their details' },
  lineage: { title: 'Lineage & History', description: 'Manage lineage sections, founder info, and martial arts history' },
  gallery: { title: 'Gallery', description: 'Manage photos and videos for the gallery page' },
  pages: { title: 'Custom Pages', description: 'Create and manage custom pages with WYSIWYG editor' },
  navigation: { title: 'Navigation Links', description: 'Manage header and footer navigation links' },
  medialibrary: { title: 'Media Library', description: 'Manage all uploaded images, organize with tags, and track usage' },
  announcements: { title: 'Announcements', description: 'Manage homepage announcement ribbon banners' },
  business: { title: 'Business Information', description: 'Update contact details and business settings' },
  testimonials: { title: 'Testimonials & Reviews', description: 'Manage customer reviews and testimonials' },
  schedule: { title: 'Weekly Schedule', description: 'Manage class schedule for each day of the week' },
  qrcodes: { title: 'QR Code Center', description: 'Generate and manage QR codes for customer engagement' },
  inbox: { title: 'Inbox', description: 'View and manage incoming emails to info@owazamartialarts.com' },
  newsletter: { title: 'Newsletter Manager', description: 'Create and send email newsletters to subscribers' },
  socialmedia: { title: 'Social Media', description: 'Create and schedule posts for Facebook, Instagram, and TikTok' },
  events: { title: 'Events Manager', description: 'Manage public events, tournaments, and seminars' },
  feedback: { title: 'Feedback Dashboard', description: 'View and analyze customer feedback submissions' },
  students: { title: 'Student Management', description: 'Manage students, belt progression, attendance, and tournaments' },
  customers: { title: 'Customer Management', description: 'Manage and authorize member accounts for portal access' },
  registrations: { title: 'Member Registrations', description: 'Review and approve new member registration requests' },
  belts: { title: 'Belt Configuration', description: 'Customize belt ranks, colors, and progression order' },
  resources: { title: 'Training Resources', description: 'Manage videos, documents, and training materials for students' },
  resourcelinks: { title: 'Resource Links', description: 'Manage shareable links that deep-link into member portal content' },
  historyreviews: { title: 'History Reviews', description: 'Review student-submitted martial arts history for approval' },
  vocabulary: { title: 'Vocabulary Manager', description: 'Manage Japanese vocabulary terms with audio' },
  quizzes: { title: 'Quiz Builder', description: 'Create and manage quizzes for student learning' },
  scenarios: { title: 'Scenario Manager', description: 'Create branching-path training scenarios' },
  badges: { title: 'Badge Manager', description: 'Create and manage achievement badges' },
  reviews: { title: 'Answer Reviews', description: 'Review and grade short answer quiz responses' },
  adminusers: { title: 'Admin Users', description: 'Manage administrator accounts and access' },
  apikeys: { title: 'API Keys', description: 'Manage external service API keys and configurations' },
  stripe: { title: 'Stripe Settings', description: 'Configure Stripe payment integration for subscriptions' },
  publish: { title: 'Publish Settings', description: 'Manage content publishing and deployment' },
  backups: { title: 'Database Backups', description: 'MongoDB backup and restore management' },
  deployments: { title: 'Deployments', description: 'Monitor and trigger deployments with auto-remediation' },
  email: { title: 'Email Settings', description: 'Manage email blocklist and forwarding addresses' },
};

// Cockpit order for display (drives sidebar, mobile drawer, and dashboard grid)
export const categoryOrder = ['students', 'learning', 'website', 'business', 'outreach', 'finance', 'settings'];

// Keep permission-filtered cockpit navigation identical everywhere it appears.
// A cockpit remains visible as long as the admin can reach at least one of its
// legacy sections; the landing page applies the same per-section gate to tiles.
export const getAccessibleCategoryIds = (canAccessSection) => categoryOrder.filter(
  (categoryId) => adminRoutes[categoryId].sections.some(
    (section) => canAccessSection(section.id)
  )
);

// Apply display preferences only after permission filtering. Hidden categories
// remain authorized and reachable by deep link; callers decide whether to include
// them (mobile's More sheet always does).
export const getCustomizedAccessibleCategoryIds = (
  canAccessSection,
  preferences = {},
  { includeHidden = false } = {},
) => {
  const accessible = new Set(getAccessibleCategoryIds(canAccessSection));
  const requestedOrder = Array.isArray(preferences.navigation_order)
    ? preferences.navigation_order
    : [];
  const ordered = [
    ...requestedOrder.filter((categoryId, index) => (
      accessible.has(categoryId) && requestedOrder.indexOf(categoryId) === index
    )),
    ...categoryOrder.filter((categoryId) => (
      accessible.has(categoryId) && !requestedOrder.includes(categoryId)
    )),
  ];

  if (includeHidden) return ordered;
  const hidden = new Set(
    Array.isArray(preferences.navigation_hidden) ? preferences.navigation_hidden : [],
  );
  return ordered.filter((categoryId) => !hidden.has(categoryId));
};
